import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { SeoTags } from "../../components/Head/SeoTags";
import { fetchPublicCollection } from "../../services/api";

const APP_URL = process.env.REACT_APP_APP_URL || "https://my.onbookmarks.com";

const safeHostname = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return url;
  }
};

const faviconUrl = (url) => {
  const host = safeHostname(url);
  return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
};

const readPreload = (uuid) => {
  const p =
    typeof window !== "undefined" && window.__PRELOADED_COLLECTION__;
  return p && p.uuid === uuid ? p : null;
};

const CollectionDetailPage = () => {
  const { uuid, domain } = useParams();

  // Prerender injects window.__PRELOADED_COLLECTION__ so the page renders with
  // real content server-side (the API blocks cross-origin fetches from the
  // build server) and the client hydrates without a mismatch.
  const preload = useRef(readPreload(uuid));
  const [collection, setCollection] = useState(preload.current);
  const [loading, setLoading] = useState(!preload.current);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (collection && collection.uuid === uuid) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setNotFound(false);
    fetchPublicCollection(uuid, domain)
      .then((res) => {
        if (!active) return;
        if (res.status === "success" && res.content) setCollection(res.content);
        else setNotFound(true);
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, domain]);

  const canonicalUrl = `https://onbookmarks.com/directory/${uuid}/${domain}`;

  if (loading) {
    return (
      <div className="directory-page">
        <div className="loading-state">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !collection) {
    return (
      <div className="directory-page">
        <SeoTags
          title="Collection not found"
          description="This bookmark collection is no longer available."
          canonical={canonicalUrl}
          localized={false}
        />
        <div className="empty-state">
          <i className="bi bi-collection"></i>
          <h3>Collection not found</h3>
          <p>This collection may be private or no longer available.</p>
          <Link to="/directory" className="btn btn-purple">
            <i className="bi bi-arrow-left me-2"></i>Browse the directory
          </Link>
        </div>
      </div>
    );
  }

  const bookmarks = collection.bookmarks || [];
  const count = bookmarks.length;
  const curator = collection.user?.firstname;
  const created = collection.created_at || collection.createdAt;
  const accent = collection.color || "#10b981";
  const description =
    collection.description ||
    `A curated collection of ${count} bookmarks shared on onBookmarks.`;

  return (
    <div className="directory-page collection-detail">
      <SeoTags
        title={`${collection.name}: Curated Bookmarks`}
        description={description}
        keywords={`${collection.name}, curated bookmarks, link collection, resources, onBookmarks`}
        canonical={canonicalUrl}
        image="/images/og-image.png"
        localized={false}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: collection.name,
            description,
            url: canonicalUrl,
            inLanguage: "en-US",
            ...(created && { dateCreated: created }),
            numberOfItems: count,
            ...(curator && { author: { "@type": "Person", name: curator } }),
            isPartOf: {
              "@type": "WebSite",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
            },
            publisher: {
              "@type": "Organization",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
            },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: count,
              itemListElement: bookmarks.slice(0, 25).map((b, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "WebPage",
                  name: b.title,
                  url: b.url,
                  ...(b.description && { description: b.description }),
                },
              })),
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://onbookmarks.com" },
              { "@type": "ListItem", position: 2, name: "Directory", item: "https://onbookmarks.com/directory" },
              { "@type": "ListItem", position: 3, name: collection.name, item: canonicalUrl },
            ],
          }),
        }}
      />

      {/* Hero */}
      <section className="collection-hero">
        <nav className="collection-breadcrumb" aria-label="breadcrumb">
          <Link to="/">Home</Link>
          <i className="bi bi-chevron-right"></i>
          <Link to="/directory">Directory</Link>
        </nav>

        <div className="collection-badge">
          <span className="dot" style={{ background: accent }}></span>
          Public collection
        </div>

        <h1>{collection.name}</h1>
        {collection.description && <p className="lead">{collection.description}</p>}

        <div className="collection-meta">
          <span className="meta-item">
            <i className="bi bi-bookmark"></i>
            {count} {count === 1 ? "bookmark" : "bookmarks"}
          </span>
          {curator && (
            <span className="meta-item">
              <i className="bi bi-person"></i>Curated by {curator}
            </span>
          )}
          {created && (
            <span className="meta-item">
              <i className="bi bi-calendar3"></i>
              {new Date(created).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
              })}
            </span>
          )}
        </div>
      </section>

      {/* Bookmarks */}
      <section className="collection-bookmarks">
        {count === 0 ? (
          <p className="text-center text-muted py-5">
            This collection has no public bookmarks yet.
          </p>
        ) : (
          <div className="bookmark-grid">
            {bookmarks.map((b) => (
              <a
                key={b.uuid || b.url}
                href={b.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="bookmark-card"
              >
                <div className="bookmark-favicon">
                  <img
                    src={faviconUrl(b.url)}
                    alt=""
                    width="20"
                    height="20"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <i className="bi bi-link-45deg"></i>
                </div>
                <div className="bookmark-body">
                  <div className="bookmark-title">{b.title || b.url}</div>
                  <div className="bookmark-domain">{safeHostname(b.url)}</div>
                  {b.description && (
                    <p className="bookmark-desc">{b.description}</p>
                  )}
                </div>
                <i className="bi bi-arrow-up-right bookmark-arrow"></i>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Build your own collection</h2>
          <p>
            Save, organize and share your bookmarks with onBookmarks, free,
            private, and across every browser.
          </p>
          <a href={`${APP_URL}/register`} className="btn btn-purple btn-lg px-5">
            Get started free
          </a>
        </div>
      </section>
    </div>
  );
};

export default CollectionDetailPage;
